<script setup lang="ts">
// A storage's form (EditDrawer): a NAS share (SMB, NFS) or a drive of the node.
import { computed, reactive, ref, watch } from 'vue'
import { api, type BlockDevice, type EditTarget, type Storage } from '../../api'
import { gbText, newId, nfsExportError, slug, smbShareError } from '../../util'
import { clone, useEditContext } from './context'

const props = defineProps<{ target: EditTarget }>()
const t = props.target
const { node, cfg } = useEditContext(t)
const storage = reactive<Storage>(
    t.kind === 'storage'
        ? clone(
              cfg.value.storage?.find((s) => s.id === t.id) ?? { id: '', name: '', kind: 'smb', server: '', share: '' },
          )
        : { id: '', name: '', kind: 'smb', server: '', share: '' },
)
const storageID = computed(
    () =>
        storage.id ||
        newId(slug(storage.name || 'nas').slice(0, 28) + '-', new Set((cfg.value.storage ?? []).map((x) => x.id))),
)
// local and USB drives the node sees, for a disk storage
const devices = ref<BlockDevice[] | null>(null)
const devicesErr = ref('')
const diskFS = ['ext4', 'xfs', 'btrfs', 'exfat', 'vfat', 'ntfs']
async function loadDevices() {
    devicesErr.value = ''
    try {
        devices.value = await api.storageDevices()
    } catch (e) {
        devicesErr.value = e instanceof Error ? e.message : String(e)
    }
}
watch(
    () => storage.kind,
    (k) => {
        if (k === 'disk' && !devices.value) void loadDevices()
    },
    { immediate: true },
)
// why a drive cannot be picked, '' when it can
function deviceBlock(d: BlockDevice): string {
    if (d.system) return 'holds the system'
    if (!diskFS.includes(d.fstype)) return `${d.fstype} is not supported`
    const other = (cfg.value.storage ?? []).find((s) => s.uuid === d.uuid && s.id !== storage.id)
    if (other) return `already storage ${other.name || other.id}`
    if (d.mountpoints.some((m) => !m.startsWith('/mnt/ese/')))
        return `mounted at ${d.mountpoints.join(', ')}: record there directly`
    return ''
}
function pickDevice(d: BlockDevice) {
    storage.uuid = d.uuid
    storage.fstype = d.fstype
    if (!storage.name) storage.name = d.label || d.model || d.path
}
const size = gbText
async function save() {
    const id = storageID.value
    await node.save(
        'storage',
        id,
        storage.kind === 'disk'
            ? { id, name: storage.name.trim(), kind: 'disk', uuid: storage.uuid, fstype: storage.fstype }
            : {
                  ...storage,
                  id,
                  name: storage.name.trim(),
                  server: storage.server?.trim(),
                  share: storage.share?.trim(),
                  uuid: undefined,
                  fstype: undefined,
                  username: storage.kind === 'smb' && storage.username ? storage.username : undefined,
                  password: storage.kind === 'smb' && storage.username ? storage.password : undefined,
                  domain: storage.kind === 'smb' && storage.domain ? storage.domain : undefined,
              },
    )
    node.select({ kind: 'storage', id })
}
// what the node would refuse, said before Save (WEB-37); nothing typed yet is not a problem yet
const shareProblem = computed(() =>
    !storage.share || storage.kind === 'disk'
        ? ''
        : storage.kind === 'smb'
          ? smbShareError(storage.share)
          : nfsExportError(storage.share),
)
defineExpose({ save, snapshot: () => JSON.stringify(storage), problem: () => shareProblem.value })
</script>

<template>
    <div class="grid grid-cols-2 gap-2.5">
        <div class="mb-2.5">
            <label>Name</label><input v-model="storage.name" required autofocus placeholder="NAS rack 1" />
        </div>
        <div class="mb-2.5">
            <label>Kind</label
            ><select v-model="storage.kind">
                <option value="smb">SMB (Windows share, most NAS)</option>
                <option value="nfs">NFS</option>
                <option value="disk">Local drive or USB drive</option>
            </select>
        </div>
    </div>
    <template v-if="storage.kind === 'disk'">
        <div class="mb-1 flex items-center gap-2">
            <span class="min-w-0 flex-1 text-xs text-fg-3"
                >Drives this node sees (a drive must already have a filesystem)</span
            ><button type="button" class="btn-ghost text-xs" @click="loadDevices">refresh</button>
        </div>
        <div v-if="devicesErr" class="text-xs text-bad">{{ devicesErr }}</div>
        <div v-else-if="devices && !devices.length" class="text-xs text-fg-3">No drives with a filesystem found.</div>
        <label
            v-for="d in devices ?? []"
            :key="d.path"
            class="mb-1 flex items-center gap-2 rounded-md border px-1.5 py-1.25 text-[13px]"
            :class="[
                deviceBlock(d) ? 'cursor-default opacity-50' : 'cursor-pointer',
                storage.uuid === d.uuid ? 'border-accent' : 'border-line',
            ]"
        >
            <input
                type="radio"
                name="dev"
                class="w-auto"
                :checked="storage.uuid === d.uuid"
                :disabled="!!deviceBlock(d)"
                @change="pickDevice(d)"
            />
            <span class="min-w-0 flex-1">
                <span>{{ d.label || d.model || d.path }}</span>
                <span class="mr-1.25 rounded-[3px] bg-bg-3 px-1 align-[1px] font-mono text-[10px] text-fg-2">{{
                    d.transport === 'usb' ? 'USB' : d.transport ? d.transport.toUpperCase() : 'drive'
                }}</span>
                <span class="text-xs text-fg-3">
                    {{ size(d.size_gb) }} · {{ d.fstype }} · {{ d.model }} ·
                    <span class="font-mono tabular-nums">{{ d.path }}</span></span
                >
                <span v-if="deviceBlock(d)" class="text-xs text-fg-3"> · {{ deviceBlock(d) }}</span>
            </span>
        </label>
        <p class="text-xs text-fg-3">
            Mounted by its filesystem's ID, whatever port it is in: at boot when it is connected, and the moment a USB
            drive is plugged in; pulled out, it is unmounted and its recordings stop instead of filling the system
            disk.<template v-if="storage.fstype && !['vfat', 'exfat', 'ntfs'].includes(storage.fstype)">
                The drive's top folder becomes the node's, so it can write there.</template
            >
            Formatting a drive is not done here.
        </p>
    </template>
    <div v-if="storage.kind !== 'disk'" class="grid grid-cols-2 gap-2.5">
        <div class="mb-2.5">
            <label>Server</label
            ><input v-model="storage.server" class="font-mono tabular-nums" placeholder="nas.local or 10.0.0.20" />
        </div>
        <div class="mb-2.5">
            <label>{{ storage.kind === 'smb' ? 'Share (optionally /folder)' : 'Export path' }}</label
            ><input
                v-model="storage.share"
                class="font-mono tabular-nums"
                :placeholder="storage.kind === 'smb' ? 'recordings' : '/volume1/recordings'"
                :aria-invalid="!!shareProblem"
            />
            <div v-if="shareProblem" class="mt-0.5 text-xs text-bad">{{ shareProblem }}</div>
        </div>
    </div>
    <div v-if="storage.kind === 'smb'" class="grid grid-cols-3 gap-2.5">
        <div class="mb-2.5">
            <label>Username <span class="text-fg-3">(empty = guest)</span></label
            ><input v-model="storage.username" autocomplete="off" />
        </div>
        <div class="mb-2.5">
            <label>Password</label
            ><input
                v-model="storage.password"
                type="password"
                autocomplete="new-password"
                title="Kept on the node and never shown again: type a new one to change it, clear it to remove it"
            />
        </div>
        <div class="mb-2.5">
            <label>Domain <span class="text-fg-3">(optional)</span></label
            ><input v-model="storage.domain" autocomplete="off" />
        </div>
    </div>
    <p v-if="storage.kind !== 'disk'" class="text-xs text-fg-3">
        The node mounts it at <span class="font-mono tabular-nums">/mnt/ese/{{ storageID }}</span> and keeps it mounted
        across reboots; its state and free space show under Storage. Record onto it by picking a folder under it in a
        recording destination: a recording never falls back to the local disk while the share is gone.<template
            v-if="storage.kind === 'nfs'"
        >
            The export must let this node's user write (for example all_squash to a user that owns the
            folder).</template
        >
    </p>
</template>
